// TestimonialsCarousel.tsx
"use client";

import Link from "next/link";
import Image from "next/image";
import book4Me from "@assets/testimonial/book4me.jpg";
import testimonialHero from "@/assets/testimonial/testimonial-hero.svg";
import HomeCarouselSection from "@/components/home/HomeCarouselSection";
import MediaCardSkeleton from "@/components/skeletons/card/MediaCardSkeleton";
import TestimonialCard from "@/components/card/TestimonialCard";
import { Button } from "@/components/ui/button";
import { SiteFrame } from "@/components/layout/SiteFrame";
import { Typography } from "@/components/Typography";
import { cn, mainPadding } from "@/lib/utils";
import { useState } from "react";
import { useGetTestimonials } from "@/hooks/testimonial/useTestimonial";
import {
  STAT_ICON,
  TESTIMONIAL_STATS,
  type TestimonialAuthorType,
  type TestimonialItem,
} from "./testimonial.type";
import { useRouter } from "next/navigation";
import { RuntimeConfig } from "@/lib/runtimeConfig";
import { useSetAtom } from "jotai";
import { dialogAtom } from "@/stores/dialogAtom";

const TESTIMONIAL_FILTERS: [
  label: string,
  authorType: TestimonialAuthorType | undefined,
][] = [
  ["All stories", undefined],
  ["Travelers", "customer"],
  ["Local partners", "merchant"],
];

/**
 * Main Component
 */
const TestimonialCarousel = () => {
  const [authorType, setAuthorType] = useState<
    TestimonialAuthorType | undefined
  >(undefined);
  // const setDialog = useSetAtom(dialogAtom);
  const { data, isFetching, isError } = useGetTestimonials({
    authorType,
    sortBy: "createdDt",
    sortOrder: "desc",
  });
  const setDialog = useSetAtom(dialogAtom);
  const router = useRouter();

  return (
    <section className="w-full bg-white py-8 md:py-10">
      <SiteFrame outerClassName={mainPadding}>
        <section className="p-4 bg-primary-muted rounded-xl shadow-product-card flex items-center justify-between gap-4 mb-8">
          <div className="flex-1">
            <Typography variant="subtitle1">
              Let Us Find Your Perfect Stay |
              ឱ្យពួកយើងជួយរកកន្លែងស្នាក់នៅត្រូវចិត្តអ្នក!
            </Typography>
            <Typography variant="body2">
              Tell us your preferences, and we'll help you find and book the
              right hotel.
            </Typography>
            <Button
              onClick={() => {
                RuntimeConfig.BFM_ENABLE === "1"
                  ? router.push("/book-for-me")
                  : setDialog({
                      id: "coming_soon_dialog",
                      props: { type: "" },
                    });
              }}
              variant={"default"}
              size={"sm"}
              className="mt-4 bg-primary"
            >
              Book for me / ជួយកក់អោយខ្ញុំ
            </Button>
          </div>

          <div className="shrink-0 flex justify-end">
            <Image
              src={book4Me}
              alt="Book for me / ជួយកក់អោយខ្ញុំ"
              width={200}
              height={200}
              className="w-30 h-auto object-contain -scale-x-100"
            />
          </div>
        </section>

        <section className="relative overflow-hidden">
          {/* Desktop: background image */}
          <Image
            src={testimonialHero}
            alt=""
            aria-hidden
            priority={false}
            className="absolute inset-0 -z-0 hidden h-full w-full object-cover object-bottom md:block"
          />
          <div className="relative z-10 mx-auto flex max-w-xl flex-col items-center gap-3 px-4 pt-6 pb-2 text-center md:py-10">
            <Typography variant="h4" mdVariant="h2" className="text-heading">
              Trusted by travelers and local partners
            </Typography>
            <Typography variant="body-xs" className="text-body md:text-[14px]">
              Real experiences from our community, from memorable trips to
              growing travel businesses across Cambodia.
            </Typography>

            <ul className="flex flex-wrap items-start justify-center gap-4 md:gap-6">
              {TESTIMONIAL_STATS.map((stat) => {
                const Icon = STAT_ICON[stat.id];
                return (
                  <li
                    key={stat.id}
                    className="flex items-center gap-2 text-left"
                  >
                    <Icon
                      className={cn(
                        "size-6 shrink-0",
                        stat.id === "rating"
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-primary",
                      )}
                    />
                    <div>
                      <Typography variant="subtitle2" className="text-heading">
                        {stat.value}
                      </Typography>
                      <Typography variant="body-xs" className="text-caption">
                        {stat.label}
                      </Typography>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Mobile: image placed cleanly under the text */}
          <div className="relative w-full md:hidden">
            <Image
              src={testimonialHero}
              alt=""
              aria-hidden
              priority={false}
              className="h-auto w-full object-contain"
            />
          </div>
        </section>

        <section className="mt-6 space-y-6">
          <div
            role="tablist"
            aria-label="Testimonial filter"
            className="flex flex-wrap justify-center gap-2"
          >
            {TESTIMONIAL_FILTERS.map(([label, type]) => (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={authorType === type}
                onClick={() => setAuthorType(type)}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-medium transition-colors text-body",
                  authorType === type ? "bg-primary" : "bg-subtle",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <HomeCarouselSection<TestimonialItem>
            title="What our community says | ស្តាប់មតិពីសហគមន៍របស់យើង"
            description="See how Tovtrip makes travel easier and helps local businesses grow with verified partners and reliable support."
            items={data}
            itemsPerCol="3-grid"
            getKey={(item) => item.id}
            renderItem={(item) => <TestimonialCard item={item} />}
            isFetching={isFetching}
            isError={isError}
            renderSkeleton={() => <MediaCardSkeleton />}
          />
        </section>

        <section className="p-4 mt-8 bg-primary-muted rounded-xl shadow-product-card flex items-center justify-between gap-4">
          <div>
            <Typography variant="subtitle1">
              Grow Your Business with Tovtrip | ពង្រីកអាជីវកម្មរបស់អ្នកជាមួយ
              Tovtrip
            </Typography>
            <Typography variant="body2">
              List your hotels, tours, rentals, or spa services and connect with
              more travelers.
            </Typography>
          </div>
          <Button
            variant={"secondary"}
            size={"sm"}
            onClick={() =>
              window.open("/partner", "_blank", "noopener,noreferrer")
            }
          >
            Become a Partner | ចូលជាដៃគូ
          </Button>
        </section>
      </SiteFrame>
    </section>
  );
};

export default TestimonialCarousel;
